import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Box,
	Typography,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { EntradaProdutoService } from "../service/entrada_produto_service";

function ModalClassificarQualidade({ open, onClose, entrada, onSave }) {
	const [loading, setLoading] = useState(false);
	const [qualidadesDisponiveis, setQualidadesDisponiveis] = useState([]);
	const [qualidades, setQualidades] = useState([]);
	const [qualidadeTemp, setQualidadeTemp] = useState({
		qualidadeColheitaId: "",
		quantidade: "",
	});

	const entradaProdutoService = new EntradaProdutoService();

	useEffect(() => {
		const loadQualidades = async () => {
			try {
				const data = await entradaProdutoService.getQualidades();
				setQualidadesDisponiveis(data || []);
			} catch (error) {
				console.error("Erro ao carregar qualidades:", error);
				alert(error.message || "Erro ao carregar qualidades");
			}
		};

		if (open && entrada) {
			console.log('ModalClassificarQualidade - entrada recebida:', entrada);
			console.log('ModalClassificarQualidade - entrada.id:', entrada.id);
			loadQualidades();
			setQualidades(entrada.qualidades?.map(q => ({
				qualidadeColheitaId: q.qualidadeColheitaId,
				quantidade: q.quantidade,
			})) || []);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, entrada]);

	const handleAdicionarQualidade = () => {
		if (!qualidadeTemp.qualidadeColheitaId || !qualidadeTemp.quantidade) {
			alert("Preencha todos os campos da qualidade");
			return;
		}

		const somaAtual = qualidades.reduce((sum, q) => sum + parseFloat(q.quantidade || 0), 0);
		const novaQuantidade = parseFloat(qualidadeTemp.quantidade);

		if (somaAtual + novaQuantidade > parseFloat(entrada?.quantidade || 0)) {
			alert("A soma das quantidades das qualidades não pode ser maior que a quantidade total da entrada");
			return;
		}

		setQualidades([...qualidades, { ...qualidadeTemp }]);
		setQualidadeTemp({ qualidadeColheitaId: "", quantidade: "" });
	};

	const handleRemoverQualidade = (index) => {
		setQualidades(qualidades.filter((_, i) => i !== index));
	};

	const handleSalvar = async () => {
		console.log('handleSalvar - entrada:', entrada);
		console.log('handleSalvar - entrada.id:', entrada?.id);

		if (qualidades.length === 0) {
			alert("Adicione pelo menos uma qualidade");
			return;
		}

		const somaQualidades = qualidades.reduce((sum, q) => sum + parseFloat(q.quantidade || 0), 0);
		if (somaQualidades > parseFloat(entrada?.quantidade || 0)) {
			alert("A soma das quantidades das qualidades não pode ser maior que a quantidade total");
			return;
		}

		// Se não tem ID, é modo cadastro - apenas fecha o modal e retorna as qualidades
		if (!entrada || !entrada.id) {
			console.log('Modo cadastro - salvando qualidades localmente');
			if (onSave) {
				onSave(qualidades);
			}
			handleClose();
			return;
		}

		// Se tem ID, é modo edição - salva no backend
		setLoading(true);
		try {
			// Converter quantidade de string para number antes de enviar
			const qualidadesFormatadas = qualidades.map(q => ({
				qualidadeColheitaId: q.qualidadeColheitaId,
				quantidade: parseFloat(q.quantidade)
			}));

			console.log('Enviando para backend - entradaId:', entrada.id);
			console.log('Enviando para backend - qualidades:', qualidadesFormatadas);

			await entradaProdutoService.salvarQualidades(entrada.id, qualidadesFormatadas);
			alert("Qualidades salvas com sucesso!");
			if (onSave) {
				onSave();
			}
			handleClose();
		} catch (error) {
			console.error("Erro ao salvar qualidades:", error);
			console.error("Erro completo:", error);
			alert(error.message || "Erro ao salvar qualidades");
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		setQualidades([]);
		setQualidadeTemp({ qualidadeColheitaId: "", quantidade: "" });
		onClose();
	};

	const getQualidadeNome = (id) => {
		return qualidadesDisponiveis.find(q => q.id === id)?.nome || "Sem qualidade";
	};

	const totalClassificado = qualidades.reduce((sum, q) => sum + parseFloat(q.quantidade || 0), 0);

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
			<DialogTitle sx={{ bgcolor: "#1a9e8f", color: "white", fontSize: "18px", fontWeight: 600 }}>
				Classificar Qualidade do Produto
			</DialogTitle>
			<DialogContent sx={{ mt: 3, px: 3 }}>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
					{/* BLOCO 1: Informações do Produto */}
					<Box sx={{ width: "100%" }}>
						<Typography variant="subtitle1" sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "16px", mb: 2 }}>
							Informações do Produto
						</Typography>
						<Box sx={{ p: 2, bgcolor: "#f9f9f9", borderRadius: 1 }}>
							<Typography variant="body1" gutterBottom sx={{ mb: 1, fontSize: "15px" }}>
								<strong>Produto:</strong> {entrada?.produtoUnidadeDistribuidora?.cultivo?.cultura}
								{entrada?.produtoUnidadeDistribuidora?.cultivo?.variedade &&
									` - ${entrada.produtoUnidadeDistribuidora.cultivo.variedade}`}
							</Typography>
							<Typography variant="body1" sx={{ fontSize: "15px" }}>
								<strong>Quantidade Total:</strong> {entrada?.quantidade}
							</Typography>
						</Box>
					</Box>

					{/* BLOCO 2: Formulário de Adicionar Qualidade */}
					<Box sx={{ width: "100%" }}>
						<Typography variant="subtitle1" sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "16px", mb: 2 }}>
							Adicionar Qualidade
						</Typography>
						<Box sx={{ display: "flex", gap: 2, width: "100%", alignItems: "flex-end" }}>
							<Box sx={{ flex: 3 }}>
								<FormControl fullWidth>
									<InputLabel style={{ fontSize: "15px" }}>Qualidade</InputLabel>
									<Select
										value={qualidadeTemp.qualidadeColheitaId}
										onChange={(e) => setQualidadeTemp({ ...qualidadeTemp, qualidadeColheitaId: e.target.value })}
										label="Qualidade"
										style={{ fontSize: "15px" }}
									>
										{qualidadesDisponiveis.map((qual) => (
											<MenuItem key={qual.id} value={qual.id} style={{ fontSize: "15px" }}>
												{qual.nome}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Box>

							<Box sx={{ flex: 1 }}>
								<TextField
									fullWidth
									label="Quantidade"
									type="number"
									value={qualidadeTemp.quantidade}
									onChange={(e) => setQualidadeTemp({ ...qualidadeTemp, quantidade: e.target.value })}
									InputLabelProps={{ style: { fontSize: "15px" } }}
									inputProps={{ style: { fontSize: "15px" }, min: 0, step: 0.01 }}
								/>
							</Box>

							<Box sx={{ flex: 1 }}>
								<Button
									fullWidth
									variant="contained"
									onClick={handleAdicionarQualidade}
									sx={{
										height: "56px",
										bgcolor: "#1a9e8f",
										"&:hover": { bgcolor: "#138a7d" },
										textTransform: "none",
										fontSize: "15px"
									}}
								>
									Adicionar
								</Button>
							</Box>
						</Box>
					</Box>

					{/* BLOCO 3: Tabela de Qualidades */}
					<Box sx={{ width: "100%" }}>
						<TableContainer>
							<Table>
								<TableHead>
									<TableRow>
										<TableCell sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "15px", borderBottom: "1px solid #e0e0e0" }}>
											Qualidade
										</TableCell>
										<TableCell sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "15px", borderBottom: "1px solid #e0e0e0", width: "25%" }}>
											Quantidade
										</TableCell>
										<TableCell sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "15px", borderBottom: "1px solid #e0e0e0", width: "120px" }} align="center">
											Ações
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{qualidades.length === 0 ? (
										<TableRow>
											<TableCell colSpan={3} align="center" sx={{ py: 4, fontSize: "15px", color: "#999" }}>
												Nenhuma qualidade adicionada
											</TableCell>
										</TableRow>
									) : (
										qualidades.map((qual, index) => (
											<TableRow key={index} sx={{ "&:hover": { bgcolor: "#f9f9f9" } }}>
												<TableCell sx={{ fontSize: "15px", borderBottom: "1px solid #f0f0f0" }}>
													{getQualidadeNome(qual.qualidadeColheitaId)}
												</TableCell>
												<TableCell sx={{ fontSize: "15px", borderBottom: "1px solid #f0f0f0" }}>
													{qual.quantidade}
												</TableCell>
												<TableCell sx={{ fontSize: "15px", borderBottom: "1px solid #f0f0f0" }} align="center">
													<IconButton
														size="small"
														onClick={() => handleRemoverQualidade(index)}
														sx={{ color: "#ff4d4d" }}
														title="Remover qualidade"
													>
														<DeleteIcon fontSize="small" />
													</IconButton>
												</TableCell>
											</TableRow>
										))
									)}
								</TableBody>
							</Table>
						</TableContainer>

						{/* Total Classificado */}
						{qualidades.length > 0 && (
							<Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
								<Typography variant="body2" sx={{ fontSize: "15px" }}>
									<strong>Total Classificado:</strong> {totalClassificado} de {entrada?.quantidade}
								</Typography>
							</Box>
						)}
					</Box>
				</Box>
			</DialogContent>
			<DialogActions sx={{ px: 3, py: 2 }}>
				<Button
					onClick={handleClose}
					disabled={loading}
					sx={{
						textTransform: "none",
						fontSize: "15px",
						color: "#666"
					}}
				>
					Cancelar
				</Button>
				<Button
					onClick={handleSalvar}
					variant="contained"
					disabled={loading || qualidades.length === 0}
					sx={{
						bgcolor: "#1a9e8f",
						"&:hover": { bgcolor: "#138a7d" },
						textTransform: "none",
						fontSize: "15px",
						px: 4
					}}
				>
					{loading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Salvar"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default ModalClassificarQualidade;
